/**
 * Casting Your Photo — namespaced extract of XTREM second-screen / users-layer behavior.
 * Only initializes when [data-casting-photo] exists.
 */
(function CastingPhotoNamespace(window, document) {
  'use strict';

  const ROOT_SELECTOR = '[data-casting-photo]';

  function readConfig(root) {
    const node = root.querySelector('[data-casting-photo-config]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.warn('[CastingPhoto] invalid config JSON', error);
      return {};
    }
  }

  function waitForGsap(callback) {
    if (window.gsap) {
      if (window.ScrollTrigger) window.gsap.registerPlugin(window.ScrollTrigger);
      callback(window.gsap);
      return;
    }
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      if (window.gsap) {
        window.clearInterval(timer);
        if (window.ScrollTrigger) window.gsap.registerPlugin(window.ScrollTrigger);
        callback(window.gsap);
      } else if (tries > 80) {
        window.clearInterval(timer);
        console.warn('[CastingPhoto] GSAP failed to load');
        callback(null);
      }
    }, 50);
  }

  /**
   * 创建第二屏实例，缓存左侧投屏、右侧长颈鹿及人物结果视频与双端播放状态。
   * @param {HTMLElement} root 第二屏根节点。
   */
  function CastingPhoto(root) {
    this.root = root;
    window.XtremAnimatedMedia.upgrade(root);
    this.config = readConfig(root);
    this.settings = Object.assign(
      {
        enableGsap: true,
        enableFade: true,
        enableScroll: false,
        enableTilt: true,
        enableMouse: true,
        animationSpeed: 1,
        animationDelay: 0,
        animationEasing: 'power2.out',
        reducedMotion: false,
      },
      this.config.settings || {}
    );
    this.leftVideo = root.querySelector('[data-casting-photo-left-video]');
    this.rightDevice = root.querySelector('[data-casting-photo-right]');
    this.initialVideo = root.querySelector('[data-casting-photo-circle-initial]');
    this.rightVideo = this.rightDevice
      ? this.rightDevice.querySelector('[data-casting-photo-circle-result]')
      : root.querySelector('[data-casting-photo-circle-result]');
    this.devices = root.querySelectorAll('[data-casting-photo-device]');
    this.title = root.querySelector('[data-casting-photo-title]');
    this.buy = root.querySelector('[data-casting-photo-buy]');
    this.desktopTrigger = root.querySelector('[data-casting-photo-trigger]');
    this.desktopMediaQuery = window.matchMedia('(min-width: 990px)');
    this.motionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.tiltTargets = root.querySelectorAll('[data-casting-photo-tilt]');
    this.destroyed = false;
    this.rafId = 0;
    this.tilt = { x: 0, z: 0, tx: 0, tz: 0 };
    this.scrollTrigger = null;
    this.timeline = null;
    this.sectionObserver = null;
    this.activeClassObserver = null;
    this.sectionInView = false;
    this.demoStarted = false;
    this.rightDemoStarted = false;
    this.resultPending = false;
    this.resultAdvancePending = false;
    this.resultAdvanceFrame = 0;
    this.resultAdvanceTouchActive = false;
    this.resultAdvanceBlockedUntil = 0;
  }

  /**
   * 判断当前是否为本次 Figma 改版覆盖的桌面视口。
   * @returns {boolean} 宽度达到桌面断点时返回 true。
   */
  CastingPhoto.prototype.isDesktopViewport = function isDesktopViewport() {
    return this.desktopMediaQuery.matches;
  };

  /**
   * 暂停并将单个视频恢复至首帧；尚未加载时由浏览器保留默认起点。
   * @param {HTMLVideoElement|HTMLCanvasElement|null} video 需要重置的视频。
   */
  CastingPhoto.prototype.resetMedia = function resetMedia(video) {
    if (!video) return;
    this.prepareMedia(video);
    this.pauseMedia(video);
    video.loop = false;
    video.removeAttribute('loop');
    try {
      video.currentTime = 0;
    } catch (error) {
      /* 未就绪的媒体从默认首帧开始，不中断后续交互。 */
    }
  };

  /**
   * 从头演示左侧投屏并恢复长颈鹿阶段；双端首次进入时自动调用，桌面点击可重播。
   */
  CastingPhoto.prototype.startDemo = function startDemo() {
    if (this.destroyed || !this.leftVideo || !this.rightDevice) return;
    this.cancelResultAdvance();
    if (this.rightDemoStarted) this.resetMedia(this.initialVideo);
    this.demoStarted = true;
    this.rightDemoStarted = false;
    this.resultPending = false;
    this.rightDevice.classList.add('is-hidden-circle');
    this.rightDevice.classList.remove('is-showing-result');
    this.resetMedia(this.leftVideo);
    this.resetMedia(this.rightVideo);
    if (this.rightVideo) this.rightVideo.preload = 'auto';
    this.syncSectionVideos(this.sectionInView);
  };

  CastingPhoto.prototype.isResultSectionActive = function isResultSectionActive() {
    if (this.destroyed || !this.sectionInView || document.hidden || !this.rightDevice) return false;
    const wrapper = this.root.closest('.scroll-transition-section');
    if (wrapper && wrapper.getAttribute('aria-hidden') === 'true') return false;
    const rect = this.root.getBoundingClientRect();
    const style = window.getComputedStyle(this.root);
    return rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight &&
      style.visibility !== 'hidden' && style.display !== 'none';
  };

  CastingPhoto.prototype.canAdvanceToResult = function canAdvanceToResult() {
    return window.matchMedia('(max-width: 767px)').matches &&
      !this.resultAdvanceTouchActive && Date.now() >= this.resultAdvanceBlockedUntil &&
      this.isResultSectionActive();
  };

  CastingPhoto.prototype.cancelResultAdvance = function cancelResultAdvance() {
    this.resultAdvancePending = false;
    if (this.resultAdvanceFrame) window.cancelAnimationFrame(this.resultAdvanceFrame);
    this.resultAdvanceFrame = 0;
  };

  /** 用户接管时只停止本区块发起的平滑滚动，不拦截手势或页面原生滚动。 */
  CastingPhoto.prototype.interruptResultAdvance = function interruptResultAdvance() {
    const advancing = Boolean(this.resultAdvanceFrame);
    this.cancelResultAdvance();
    if (!advancing) return;

    window.scrollTo({ top: window.scrollY || window.pageYOffset || 0, behavior: 'instant' });
    if (this.rightDemoStarted && this.isResultSectionActive()) this.playMedia(this.rightVideo);
  };

  CastingPhoto.prototype.initResultAdvanceInteraction = function initResultAdvanceInteraction() {
    if (!this.rightDevice) return;

    this.onResultAdvanceTouchStart = () => {
      if (this.destroyed) return;
      this.resultAdvanceTouchActive = true;
      this.resultAdvanceBlockedUntil = Date.now() + 400;
      this.interruptResultAdvance();
    };
    this.onResultAdvanceTouchEnd = (event) => {
      if (this.destroyed) return;
      this.resultAdvanceTouchActive = Boolean(event.touches && event.touches.length);
      this.resultAdvanceBlockedUntil = Date.now() + 400;
    };
    this.onResultAdvanceWheel = () => {
      if (this.destroyed) return;
      this.resultAdvanceBlockedUntil = Date.now() + 400;
      this.interruptResultAdvance();
    };
    this.onResultAdvanceScroll = () => {
      if (this.destroyed || this.resultAdvanceFrame) return;
      // 手指离开后的惯性滚动也需停稳；本区块自己的自动滚动不延长等待。
      this.resultAdvanceBlockedUntil = Date.now() + 400;
    };

    document.addEventListener('touchstart', this.onResultAdvanceTouchStart, { passive: true, capture: true });
    document.addEventListener('touchend', this.onResultAdvanceTouchEnd, { passive: true, capture: true });
    document.addEventListener('touchcancel', this.onResultAdvanceTouchEnd, { passive: true, capture: true });
    window.addEventListener('wheel', this.onResultAdvanceWheel, { passive: true, capture: true });
    window.addEventListener('scroll', this.onResultAdvanceScroll, { passive: true });
  };

  CastingPhoto.prototype.advanceToResult = function advanceToResult() {
    const pending = this.resultAdvancePending;
    this.resultAdvancePending = false;
    if (!pending || !this.canAdvanceToResult()) return false;

    const viewportHeight = window.innerHeight || 1;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const deviceRect = this.rightDevice.getBoundingClientRect();
    const wrapper = this.root.closest('#MainContent > .scroll-transition-section') || this.root;
    const sectionRect = wrapper.getBoundingClientRect();
    if (deviceRect.height <= 0 || sectionRect.height <= 0) return false;
    if (deviceRect.top >= 20 && deviceRect.bottom <= viewportHeight - 20) return false;

    const sectionTop = scrollY + sectionRect.top;
    const sectionEnd = Math.max(sectionTop, scrollY + sectionRect.bottom - viewportHeight);
    const centeredTop = scrollY + deviceRect.top - Math.max(20, (viewportHeight - deviceRect.height) / 2);
    const targetTop = Math.max(sectionTop, Math.min(sectionEnd, centeredTop));
    if (targetTop <= scrollY + 2) return false;

    if (this.settings.reducedMotion || this.motionMediaQuery.matches) {
      window.scrollTo({ top: targetTop, behavior: 'instant' });
      return false;
    }

    const startedAt = Date.now();
    let lastScrollY = scrollY;
    let stableFrames = 0;
    const finishScroll = () => {
      this.resultAdvanceFrame = 0;
      if (!this.isResultSectionActive() || !this.rightDemoStarted) return;
      if (!window.matchMedia('(max-width: 767px)').matches) {
        this.syncSectionVideos(this.sectionInView);
        return;
      }
      const currentScrollY = window.scrollY || window.pageYOffset || 0;
      stableFrames = Math.abs(currentScrollY - lastScrollY) < 1 ? stableFrames + 1 : 0;
      lastScrollY = currentScrollY;
      const arrived = Math.abs(currentScrollY - targetTop) <= 2;
      const elapsed = Date.now() - startedAt;
      if ((arrived || elapsed >= 1200) && stableFrames >= 3) {
        const rect = this.rightDevice.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < window.innerHeight) this.playMedia(this.rightVideo);
        return;
      }
      if (elapsed >= 5000) return;
      this.resultAdvanceFrame = window.requestAnimationFrame(finishScroll);
    };
    this.resultAdvanceFrame = window.requestAnimationFrame(finishScroll);
    window.scrollTo({ top: targetTop, behavior: 'smooth' });
    return true;
  };

  /** 人物结果首帧就绪后再换层，慢网络下先保留长颈鹿，避免圆屏出现空白。 */
  CastingPhoto.prototype.showCastResult = function showCastResult() {
    if (this.destroyed || !this.resultPending || !this.rightVideo || this.rightVideo.readyState < 2) return;
    this.resultPending = false;
    this.rightDemoStarted = true;
    this.pauseMedia(this.initialVideo);
    this.rightDevice.classList.remove('is-hidden-circle');
    this.rightDevice.classList.add('is-showing-result');
    const advancing = this.advanceToResult();
    if (this.sectionInView && !advancing) this.playMedia(this.rightVideo);
  };

  /** 初始化两阶段圆屏联动；两端均先展示长颈鹿，左侧播放完才允许切换人物结果。 */
  CastingPhoto.prototype.initCircleSwap = function initCircleSwap() {
    if (!this.rightDevice) return;
    this.resetMedia(this.initialVideo);
    this.resetMedia(this.leftVideo);
    this.resetMedia(this.rightVideo);
    this.rightDevice.classList.add('is-hidden-circle');
    this.rightDevice.classList.remove('is-showing-result');

    /** 桌面点击可重播完整演示；双端首次进入均由可见性逻辑自动启动。 */
    this.onDesktopTrigger = () => {
      if (this.isDesktopViewport()) this.startDemo();
    };

    /** 左侧真正结束才请求人物阶段，结果素材尚未就绪时继续显示长颈鹿。 */
    this.onLeftEnded = () => {
      if (this.destroyed || !this.demoStarted || !this.rightVideo || this.resultPending || this.rightDemoStarted) return;
      this.resultAdvancePending = this.canAdvanceToResult();
      this.resultPending = true;
      this.rightVideo.preload = 'auto';
      this.showCastResult();
    };

    /** 预加载完成后仅兑现尚未取消的结果切换，避免重播时迟到回调提前显示人物。 */
    this.onResultReady = () => this.showCastResult();

    /** 窗口跨越断点或减少动画偏好变化时同步当前阶段，不重置投屏进度。 */
    this.onPlaybackModeChange = () => this.syncSectionVideos(this.sectionInView);

    this.desktopTrigger?.addEventListener('click', this.onDesktopTrigger);
    this.leftVideo?.addEventListener('ended', this.onLeftEnded);
    this.rightVideo?.addEventListener('loadeddata', this.onResultReady);
    this.rightVideo?.addEventListener('canplay', this.onResultReady);
    this.desktopMediaQuery.addEventListener('change', this.onPlaybackModeChange);
    this.motionMediaQuery.addEventListener('change', this.onPlaybackModeChange);
    document.addEventListener('visibilitychange', this.onPlaybackModeChange);
  };

  /** 设置静音和内联播放属性，避免新素材自带音轨或手机全屏行为干扰演示。 */
  CastingPhoto.prototype.prepareMedia = function prepareMedia(video) {
    if (!video) return;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
  };

  /** 播放未完成的视频；结果播完后保持末帧，自动播放受限时保留画面而不抛出未处理异常。 */
  CastingPhoto.prototype.playMedia = function playMedia(video) {
    if (this.destroyed || document.hidden || !video || video.ended || typeof video.play !== 'function') return;
    if (video.paused === false) return;
    this.prepareMedia(video);
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      /** 浏览器拒绝自动播放时保留当前帧，允许下一次可见或点击事件重试。 */
      playPromise.catch(() => undefined);
    }
  };

  CastingPhoto.prototype.pauseMedia = function pauseMedia(video) {
    if (!video || typeof video.pause !== 'function') return;
    try {
      video.pause();
    } catch (e) {
      /* noop */
    }
  };

  CastingPhoto.prototype.isSectionVisible = function isSectionVisible(entry) {
    if (!entry || !entry.isIntersecting || this.root.closest('[hidden], [aria-hidden="true"]')) return false;
    const style = window.getComputedStyle(this.root);
    if (style.visibility === 'hidden' || style.display === 'none') return false;
    const rect = this.root.getBoundingClientRect();
    return rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
  };

  /**
   * 根据可见性同步三个视频；双端首次进入自动启动，离屏或后台暂停，完成后保持结果。
   * @param {boolean} isVisible 第二屏当前是否可见。
   */
  CastingPhoto.prototype.syncSectionVideos = function syncSectionVideos(isVisible) {
    if (this.destroyed) return;
    this.sectionInView = isVisible;

    if (!isVisible || document.hidden) {
      this.cancelResultAdvance();
      this.pauseMedia(this.leftVideo);
      this.pauseMedia(this.initialVideo);
      this.pauseMedia(this.rightVideo);
      return;
    }

    if (!this.demoStarted && this.leftVideo) {
      this.startDemo();
      return;
    }

    if (this.demoStarted) this.playMedia(this.leftVideo);
    else this.pauseMedia(this.leftVideo);

    if (this.rightDemoStarted) {
      this.pauseMedia(this.initialVideo);
      if (!this.resultAdvanceFrame) this.playMedia(this.rightVideo);
      else this.pauseMedia(this.rightVideo);
    } else {
      this.pauseMedia(this.rightVideo);
      if (!this.settings.reducedMotion && !this.motionMediaQuery.matches) this.playMedia(this.initialVideo);
      else {
        this.pauseMedia(this.initialVideo);
        if (this.initialVideo) this.initialVideo.preload = 'auto';
      }
    }
  };

  /** 观察第二屏可见性并管理三个视频的离屏暂停，沿用原有整屏切换观察器。 */
  CastingPhoto.prototype.initViewportPlayback = function initViewportPlayback() {
    if (this.sectionObserver) {
      this.sectionObserver.disconnect();
      this.sectionObserver = null;
    }
    if (this.activeClassObserver) {
      this.activeClassObserver.disconnect();
      this.activeClassObserver = null;
    }

    this.prepareMedia(this.rightVideo);
    this.prepareMedia(this.initialVideo);
    this.prepareMedia(this.leftVideo);

    if (!this.rightVideo && !this.leftVideo && !this.initialVideo) return;

    const target =
      this.root.closest('#MainContent > .scroll-transition-section') || this.root;

    const applyVisibility = (intersecting) => {
      if (this.destroyed) return;
      this.syncSectionVideos(this.isSectionVisible({ isIntersecting: intersecting }));
    };

    if (typeof IntersectionObserver !== 'function') {
      this.onViewportFallback = () => {
        const rect = target.getBoundingClientRect();
        applyVisibility(rect.bottom > 0 && rect.top < window.innerHeight);
      };
      window.addEventListener('scroll', this.onViewportFallback, { passive: true });
      window.addEventListener('resize', this.onViewportFallback);
      this.onViewportFallback();
    } else {
      this.sectionObserver = new IntersectionObserver(
        (entries) => {
          applyVisibility(Boolean(entries[0] && entries[0].isIntersecting));
        },
        { root: null, threshold: 0.25 }
      );
      this.sectionObserver.observe(target);
    }

    if (typeof MutationObserver === 'function' && target !== this.root) {
      this.activeClassObserver = new MutationObserver(() => {
        const rect = target.getBoundingClientRect();
        const viewportHeight = window.innerHeight || 1;
        applyVisibility(rect.bottom > 0 && rect.top < viewportHeight);
      });
      this.activeClassObserver.observe(target, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
  };

  CastingPhoto.prototype.initEntrance = function initEntrance(gsap) {
    if (!gsap || !this.settings.enableGsap || this.settings.reducedMotion) {
      this.root.querySelectorAll('[data-casting-photo-animate]').forEach((el) => {
        el.style.opacity = '1';
        el.style.visibility = 'visible';
      });
      return;
    }

    const speed = this.settings.animationSpeed || 1;
    const delay = this.settings.animationDelay || 0;
    const ease = this.settings.animationEasing || 'power2.out';
    const left = this.root.querySelector('[data-casting-photo-left]');
    const right = this.root.querySelector('[data-casting-photo-right]');

    gsap.set([this.title, this.buy, left, right].filter(Boolean), {
      autoAlpha: this.settings.enableFade ? 0 : 1,
    });

    this.timeline = gsap.timeline({
      defaults: { ease, force3D: true },
      delay,
    });

    if (this.title) {
      this.timeline.fromTo(
        this.title,
        { autoAlpha: 0, y: 0, scale: 1 },
        { autoAlpha: 1, y: 0, scale: 1, duration: 0.1 / speed },
        0
      );
    }

    if (this.buy) {
      this.timeline.fromTo(
        this.buy,
        { autoAlpha: 0, y: 0, scale: 1 },
        { autoAlpha: 1, y: 0, scale: 1, duration: 0.08 / speed },
        0.04 / speed
      );
    }

    if (left || right) {
      this.timeline.fromTo(
        [left, right].filter(Boolean),
        { autoAlpha: 0, x: -34, scale: 0.94 },
        { autoAlpha: 1, x: 0, scale: 1, duration: 0.26 / speed },
        0.03 / speed
      );
    }

    if (this.settings.enableScroll && window.ScrollTrigger) {
      this.scrollTrigger = ScrollTrigger.create({
        trigger: this.root,
        start: 'top 75%',
        once: true,
        animation: this.timeline,
      });
      this.timeline.pause(0);
    }
  };

  CastingPhoto.prototype.initTilt = function initTilt() {
    if (!this.settings.enableTilt || !this.settings.enableMouse || this.settings.reducedMotion) return;
    if (!this.tiltTargets.length) return;

    const onMove = (clientX, clientY) => {
      const x = clientX / window.innerWidth - 0.5;
      const y = clientY / window.innerHeight - 0.5;
      this.tilt.tx = Math.max(-0.2, Math.min(0.2, y * 0.4));
      this.tilt.tz = Math.max(-0.08, Math.min(0.08, -x * 0.14));
    };

    const onLeave = () => {
      this.tilt.tx = 0;
      this.tilt.tz = 0;
    };

    this.onPointerMove = (event) => onMove(event.clientX, event.clientY);
    this.onPointerLeave = onLeave;
    this.onTouchMove = (event) => {
      if (!event.touches?.[0]) return;
      onMove(event.touches[0].clientX, event.touches[0].clientY);
    };

    window.addEventListener('pointermove', this.onPointerMove, { passive: true });
    window.addEventListener('pointerleave', this.onPointerLeave, { passive: true });
    this.root.addEventListener('touchmove', this.onTouchMove, { passive: true });
    this.root.addEventListener('touchend', this.onPointerLeave, { passive: true });

    const tick = () => {
      if (this.destroyed) return;
      this.tilt.x += (this.tilt.tx - this.tilt.x) * 0.08;
      this.tilt.z += (this.tilt.tz - this.tilt.z) * 0.08;
      this.tiltTargets.forEach((el) => {
        el.style.transform = `rotateX(${this.tilt.x * 57.3}deg) rotateZ(${this.tilt.z * 57.3}deg)`;
      });
      this.rafId = window.requestAnimationFrame(tick);
    };
    this.rafId = window.requestAnimationFrame(tick);
  };

  /**
   * 销毁第二屏实例，暂停三个视频并释放媒体、断点、可见性与交互监听，避免编辑器重复加载。
   */
  CastingPhoto.prototype.destroy = function destroy() {
    this.destroyed = true;
    this.cancelResultAdvance();
    if (this.onResultAdvanceTouchStart) {
      document.removeEventListener('touchstart', this.onResultAdvanceTouchStart, true);
    }
    if (this.onResultAdvanceTouchEnd) {
      document.removeEventListener('touchend', this.onResultAdvanceTouchEnd, true);
      document.removeEventListener('touchcancel', this.onResultAdvanceTouchEnd, true);
    }
    if (this.onResultAdvanceWheel) {
      window.removeEventListener('wheel', this.onResultAdvanceWheel, true);
    }
    if (this.onResultAdvanceScroll) {
      window.removeEventListener('scroll', this.onResultAdvanceScroll);
    }
    if (this.rafId) window.cancelAnimationFrame(this.rafId);
    if (this.timeline) this.timeline.kill();
    if (this.scrollTrigger) this.scrollTrigger.kill();
    if (this.sectionObserver) {
      this.sectionObserver.disconnect();
      this.sectionObserver = null;
    }
    if (this.activeClassObserver) {
      this.activeClassObserver.disconnect();
      this.activeClassObserver = null;
    }
    this.pauseMedia(this.rightVideo);
    this.pauseMedia(this.initialVideo);
    this.pauseMedia(this.leftVideo);
    if (this.leftVideo && this.onLeftEnded) {
      this.leftVideo.removeEventListener('ended', this.onLeftEnded);
    }
    if (this.rightVideo && this.onResultReady) {
      this.rightVideo.removeEventListener('loadeddata', this.onResultReady);
      this.rightVideo.removeEventListener('canplay', this.onResultReady);
    }
    if (this.onPlaybackModeChange) {
      this.desktopMediaQuery.removeEventListener('change', this.onPlaybackModeChange);
      this.motionMediaQuery.removeEventListener('change', this.onPlaybackModeChange);
      document.removeEventListener('visibilitychange', this.onPlaybackModeChange);
    }
    if (this.desktopTrigger && this.onDesktopTrigger) {
      this.desktopTrigger.removeEventListener('click', this.onDesktopTrigger);
    }
    if (this.onPointerMove) window.removeEventListener('pointermove', this.onPointerMove);
    if (this.onPointerLeave) window.removeEventListener('pointerleave', this.onPointerLeave);
    if (this.onTouchMove) this.root.removeEventListener('touchmove', this.onTouchMove);
    if (this.onViewportFallback) {
      window.removeEventListener('scroll', this.onViewportFallback);
      window.removeEventListener('resize', this.onViewportFallback);
    }
    window.XtremAnimatedMedia?.destroy?.(this.root);
  };

  CastingPhoto.prototype.init = function init(gsap) {
    this.initResultAdvanceInteraction();
    this.initCircleSwap();
    this.initViewportPlayback();
    this.initEntrance(gsap);
    this.initTilt();
  };

  function boot() {
    const roots = document.querySelectorAll(ROOT_SELECTOR);
    if (!roots.length) return;

    waitForGsap((gsap) => {
      roots.forEach((root) => {
        if (root.__castingPhoto) return;
        const instance = new CastingPhoto(root);
        root.__castingPhoto = instance;
        instance.init(gsap);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target?.querySelector?.(ROOT_SELECTOR) || event.target?.matches?.(ROOT_SELECTOR)) {
      boot();
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target?.matches?.(ROOT_SELECTOR)
      ? event.target
      : event.target?.querySelector?.(ROOT_SELECTOR);
    if (root?.__castingPhoto) {
      root.__castingPhoto.destroy();
      delete root.__castingPhoto;
    }
  });

  window.CastingPhoto = CastingPhoto;
})(window, document);


